/**
 * Calendar-day validation for all-day meeting UI (not API all-day storage anchors).
 *
 * Compares `YYYY-MM-DD` strings in an explicit IANA timezone — never instant `< now` alone.
 */

export type AllDayPastField = 'start' | 'end';

/**
 * Default timezone for meeting validation: browser-reported zone in client code, `UTC` in Node/tests without `Intl` support.
 */
export function resolveBrowserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === 'string' && tz.length > 0) return tz;
  } catch {
    // ignore
  }
  return 'UTC';
}

/**
 * Calendar `YYYY-MM-DD` for an instant as seen in `timeZone` (en-CA / ISO date order).
 */
export function getCalendarDayInTimeZone(date: Date, timeZone: string): string | null {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  if (typeof timeZone !== 'string' || timeZone.length === 0) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * True when `candidate`'s calendar day in `timeZone` is the same as or after `now`'s calendar day there.
 */
export function isDateOnOrAfterCalendarTodayInTimeZone(
  candidate: Date,
  now: Date,
  timeZone: string
): boolean {
  const todayStr = getCalendarDayInTimeZone(now, timeZone);
  const candStr = getCalendarDayInTimeZone(candidate, timeZone);
  if (!todayStr || !candStr) return false;
  return candStr >= todayStr;
}

/**
 * For all-day selections: returns `'start'` or `'end'` if that edge is strictly before today's calendar day in the zone; otherwise `null`.
 * Start is checked before end so the UI can highlight the primary field first.
 */
export function getAllDayPastField(options: {
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  now?: Date;
  timeZone?: string;
}): AllDayPastField | null {
  const now = options.now ?? new Date();
  const tz =
    options.timeZone && options.timeZone.length > 0 ? options.timeZone : resolveBrowserTimeZone();
  const todayStr = getCalendarDayInTimeZone(now, tz);
  if (!todayStr) return null;

  if (options.startDate) {
    const s = getCalendarDayInTimeZone(options.startDate, tz);
    if (s !== null && s < todayStr) return 'start';
  }
  if (options.endDate) {
    const e = getCalendarDayInTimeZone(options.endDate, tz);
    if (e !== null && e < todayStr) return 'end';
  }
  return null;
}
